import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import {
  Activity,
  Clock,
  Phone,
  Calendar,
  RefreshCw,
  User,
  LogIn,
  MonitorSmartphone,
  Globe,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserAttendance = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [summary, setSummary] = useState(null);
  const [loginAudit, setLoginAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [auditStartDate, setAuditStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [auditEndDate, setAuditEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [auditUserId, setAuditUserId] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchUsers();
    fetchSummary();
  }, [isAdmin, navigate]);

  useEffect(() => {
    fetchSummary();
  }, [selectedDate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/attendance/summary?date=${selectedDate}`);
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load attendance summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginAudit = async () => {
    setLoadingAudit(true);
    try {
      let url = `${API}/attendance/login-audit?start_date=${auditStartDate}&end_date=${auditEndDate}`;
      if (auditUserId) {
        url += `&user_id=${auditUserId}`;
      }
      const response = await axios.get(url);
      setLoginAudit(response.data);
    } catch (error) {
      toast.error('Failed to load login audit');
    } finally {
      setLoadingAudit(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return format(date, 'h:mm a');
    } catch {
      return '-';
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM d, h:mm a');
    } catch {
      return '-';
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-6" data-testid="user-attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Activity className="w-8 h-8" />
            User Attendance
          </h1>
          <p className="text-muted-foreground mt-1">Monitor user logins and daily activity</p>
        </div>
        <Button variant="outline" onClick={() => { fetchSummary(); }} className="border-border" data-testid="refresh-attendance">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <Tabs defaultValue="summary" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="summary">Daily Summary</TabsTrigger>
          <TabsTrigger value="audit">Login Audit</TabsTrigger>
        </TabsList>

        {/* Daily Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          {/* Date Picker */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm">Date:</Label>
                </div>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-secondary border-border w-48"
                  data-testid="summary-date-input"
                />
                <Badge variant="outline" className="ml-auto">
                  {summary?.summary?.length || 0} users
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {summary?.summary?.map(user => (
                <Card 
                  key={user.user_id} 
                  className={`bg-card border-border ${user.is_active ? 'border-emerald-500/30' : ''}`}
                  data-testid={`attendance-user-${user.user_id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${user.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-primary/20 text-primary'}`}>
                          {user.user_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            {user.user_name}
                            {user.is_active && (
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Active</Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs">{user.user_email}</CardDescription>
                        </div>
                      </div>
                      <Badge variant={user.role === 'owner' ? 'default' : 'secondary'} className="text-xs">
                        {user.role === 'owner' ? 'Admin' : 'Setter'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-2 bg-secondary/30 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                          <LogIn className="w-3 h-3" />
                          First Login
                        </div>
                        <p className="font-medium text-sm mt-1">
                          {formatTime(user.first_login_today)}
                        </p>
                      </div>
                      <div className="p-2 bg-secondary/30 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          Last Seen
                        </div>
                        <p className="font-medium text-sm mt-1">
                          {formatTime(user.last_seen)}
                        </p>
                      </div>
                      <div className="p-2 bg-secondary/30 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                          <LogIn className="w-3 h-3" />
                          Logins
                        </div>
                        <p className="font-medium text-sm mt-1">
                          {user.login_count_today || 0}
                        </p>
                      </div>
                      <div className="p-2 bg-secondary/30 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          Calls
                        </div>
                        <p className="font-medium text-sm mt-1 text-blue-400">
                          {user.calls_today || 0}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-primary/10 rounded-lg text-center">
                      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        Appointments Booked
                      </div>
                      <p className="font-bold text-lg text-primary mt-1">
                        {user.appointments_booked_today || 0}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && (!summary?.summary || summary.summary.length === 0) && (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">No attendance data for this date</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Login Audit Tab */}
        <TabsContent value="audit" className="space-y-4">
          {/* Filters */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Start Date</Label>
                  <Input
                    type="date"
                    value={auditStartDate}
                    onChange={(e) => setAuditStartDate(e.target.value)}
                    className="bg-secondary border-border w-40"
                    data-testid="audit-start-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">End Date</Label>
                  <Input
                    type="date"
                    value={auditEndDate}
                    onChange={(e) => setAuditEndDate(e.target.value)}
                    className="bg-secondary border-border w-40"
                    data-testid="audit-end-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">User</Label>
                  <Select value={auditUserId || "all"} onValueChange={(v) => setAuditUserId(v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-secondary border-border w-48" data-testid="audit-user-select">
                      <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All users</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchLoginAudit} disabled={loadingAudit} data-testid="load-audit-btn">
                  {loadingAudit ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
                  <span className="ml-2">Load Logs</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Audit Log List */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <LogIn className="w-5 h-5" />
                Login Events
                <Badge variant="secondary" className="ml-2">{loginAudit.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {loginAudit.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    {loadingAudit ? 'Loading...' : 'No login events found. Click "Load Logs" to fetch data.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {loginAudit.map((entry, index) => (
                      <div 
                        key={entry.id || index}
                        className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-border"
                        data-testid={`audit-entry-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                            {entry.user_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{entry.user_name}</span>
                              <Badge variant={entry.role === 'owner' ? 'default' : 'secondary'} className="text-xs">
                                {entry.role === 'owner' ? 'Admin' : 'Setter'}
                              </Badge>
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                                {entry.event_type}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{entry.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatDateTime(entry.timestamp)}</p>
                          {entry.ip_address && (
                            <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                              <Globe className="w-3 h-3" />
                              {entry.ip_address}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserAttendance;
